<script lang="ts">
	//Helpers
	import { capFirstChar, deepCopy } from 'src/helpers/utilities';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import {
		createPeripheralDevice,
		deletePeripheralDevice,
		getPeripheralDevice,
	} from 'src/helpers/addons/addon_device_helpers';
	import {
		getPeripheralSettings,
		savePeripheralSettings,
	} from 'src/helpers/addons/addon_settings_helpers';
	import { proRS485AddonSupport } from 'src/helpers/addons/addon_support_helpers';
	import { postReq } from 'src/services/http';
	//Design system components
	import { Text } from '@Shelly/shelly-design-system';
	//Components
	import DigitalInputIcons from './DigitalInputSettings/DigitalInputIcons.svelte';
	import SwitchOutputTimers from './SwitchOutputSettings/SwitchOutputTimers.svelte';
	import SwitchOutputTimersV2 from './SwitchOutputSettings/SwitchOutputTimersV2.svelte';
	import PeripheralExtractAsDevice from './PeripheralExtractAsDevice.svelte';
	import SerialSettings from './SerialSettings/SerialSettings.svelte';
	import PeripheralDeviceSetting from './PeripheralDevice/PeripheralDeviceSetting.svelte';
	import RS485SerialSettings from './SerialSettings/RS485SerialSettings.svelte';
	//Stores
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	//Types
	import type { Peripheral } from 'src/types/Peripherals';
	import type { Device } from 'src/types/Device';
	import type { SettingRequest } from 'src/types/requests/settings/setting';

	export let device: Device, peripheral: Peripheral, settingsErrors: boolean;

	let timersError = false;
	let settingsError = false;
	let digitalInputIcons: DigitalInputIcons;
	let settings = deepCopy(peripheral.settings);

	const peripheralSettings = getPeripheralSettings(peripheral, settings);
	const peripheralDevice = getPeripheralDevice(device.id, peripheral);
	let extractAsDevice = peripheralDevice != null;

	$: settingsErrors = settingsError || timersError;

	export const save = async () => {
		const res: SettingRequest = await savePeripheralSettings(device, peripheral, settings);
		if (res?.isok) {
			const peripheralDevice: Device = getPeripheralDevice(device.id, peripheral);
			if (digitalInputIcons) {
				await digitalInputIcons.save();
			}
			if (extractAsDevice && !peripheralDevice) {
				peripheral.settings.name = settings.name;
				await createPeripheralDevice(device, peripheral);
			} else if (!extractAsDevice && peripheralDevice) {
				await deletePeripheralDevice(peripheralDevice);
			} else if (peripheralDevice) {
				if (settings.name) {
					peripheralDevice.name = settings.name;
				}
				await postReq('interface/device/save', {
					id: peripheralDevice.id,
					data: JSON.stringify(peripheralDevice),
				});
			}
		}
		onSettingsResponse(res, device);
		return res;
	};
</script>

{#if peripheral.peripheralType === 'serial'}
	{#if proRS485AddonSupport(device)}
		<RS485SerialSettings {peripheral} bind:settings bind:error={settingsError} />
	{:else}
		<SerialSettings {device} {peripheral} bind:settings />
	{/if}
{:else}
	{#each peripheralSettings as { title, settingType }}
		{#if !/^(temperature_units)$/.test(settingType)}
			<Text styleType="h2" class="mb-s">
				{capFirstChar(title)}
			</Text>
			<PeripheralDeviceSetting
				class="mb-m"
				{device}
				{peripheral}
				{settingType}
				hideSaveButton
				bind:settings
				bind:settingsError
				bind:digitalInputIcons
			/>
		{/if}
	{/each}
	{#if peripheral.peripheralType === 'switch'}
		{#if $showNewLayout}
			<SwitchOutputTimersV2 class="mb-m" bind:settings bind:error={timersError} />
		{:else}
			<SwitchOutputTimers class="mb-m" bind:settings bind:error={timersError} />
		{/if}
	{/if}

	<PeripheralExtractAsDevice bind:extractAsDevice />
{/if}
